/**
 * Does closing this modal throw away something the user typed?
 *
 * ─── WHY ────────────────────────────────────────────────────────────────────
 * openModal() closed on Escape, on a click outside the card, and on Cancel —
 * all three immediately, with no question asked. On 2026-09-17 that lost a
 * dictated note during testing: the text reached the textarea, the modal
 * closed, and the note was gone. Nothing had saved it and nothing had warned.
 *
 * For a designer dictating a long note on a phone, a stray tap on the dimmed
 * backdrop is an easy mistake and an expensive one.
 *
 * ─── WHY READ VALUES HERE ───────────────────────────────────────────────────
 * The snapshot taken on open and the values read on submit must be produced by
 * the SAME code. If they diverged — say submit coerced a checkbox one way and
 * the snapshot another — every untouched modal would report itself dirty and
 * the guard would nag on every Cancel until it got ignored.
 */

export interface ReadableField {
  name: string;
  type?: string;
}

export type ModalValues = Record<string, string | boolean>;

/**
 * Read a modal's values from its form data.
 *
 * `get` is FormData.get in practice; it's a parameter so this stays testable
 * without a DOM.
 */
export function readModalValues(
  fields: ReadableField[],
  get: (name: string) => FormDataEntryValue | null,
): ModalValues {
  const values: ModalValues = {};
  for (const f of fields) {
    if (f.type === 'checkbox') values[f.name] = get(f.name) === 'on';
    else values[f.name] = String(get(f.name) ?? '');
  }
  return values;
}

/**
 * True when anything differs from the snapshot taken on open.
 *
 * Text is compared TRIMMED: typing a space and deleting it, or a stray trailing
 * newline, isn't content worth a confirmation. A guard that fires on nothing
 * trains people to click through it, and then it can't protect anything.
 */
export function hasUnsavedChanges(initial: ModalValues, current: ModalValues): boolean {
  const keys = new Set([...Object.keys(initial), ...Object.keys(current)]);
  for (const k of keys) {
    const a = initial[k];
    const b = current[k];
    if (typeof a === 'boolean' || typeof b === 'boolean') {
      if (a !== b) return true;
      continue;
    }
    if ((a ?? '').trim() !== (b ?? '').trim()) return true;
  }
  return false;
}

export const DISCARD_PROMPT = 'Discard your unsaved changes?';

/**
 * What to tell someone whose save just failed.
 *
 * Supabase hands back plain error OBJECTS, not `Error` instances, so a check
 * for `instanceof Error` sent every save failure in the app to one useless
 * line — "Something went wrong" — offline or not (found 2026-09-19). The
 * person's text is still in the form in every case, so say so.
 */
export function saveErrorMessage(err: unknown, online: boolean): string {
  if (!online) {
    return 'You’re offline, so nothing was saved. Your text is still here — save again once you’re back online.';
  }
  const message = typeof (err as { message?: unknown } | null)?.message === 'string'
    ? ((err as { message: string }).message).trim()
    : '';
  // How a request that never reached the server reads in Chrome, Safari and
  // Firefox, whether raw or wrapped by supabase-js.
  if (/failed to fetch|load failed|networkerror|network request failed|fetch failed/i.test(message)) {
    return 'Couldn’t reach the server, so nothing was saved. Check your connection and try again — your text is still here.';
  }
  return message || 'Couldn’t save. Please try again — your text is still here.';
}
